import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import FileTextOutlined from '@ant-design/icons/FileTextOutlined'
import UploadOutlined from '@ant-design/icons/UploadOutlined'
import { Button, Modal, Tooltip, Typography, Upload, message } from 'antd'
import { inject, observer } from 'mobx-react'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { redPrimary } from '../../../color'
import { trimOverLengthString } from '../../../components/Common/CellText'
import ModalAnnexSign from '../../../components/PopupSign/ModalAnnexSign'
import PopupRegisNumberAnnexDoc from './CanvasDoc/PopupRegisNumber/PopupRegisNumberAnnexDoc'
import {
  FileAction,
  FileItem,
  SignDocumentList,
} from './InternalDocumentSignDocDetailPageStyled'

const InternalDocumentSignDocUpdateAnnex = ({
  visible,
  onClose,
  selectedElectronicDocument,
  fileStore,
  loadingAnimationStore,
  electronicDocumentStore,
  getElectronicDocumentById,
  signedDocumentStore,
  listHistoryLogStore,
  commonFileStore,
  currentStep,
}) => {
  const [fileList, setFileList] = useState([])
  const [isRegisNumberModalVisible, setIsRegisNumberModalVisible] = useState(
    false
  )
  const [isSignModalVisible, setIsSignModalVisible] = useState(false)
  const [activeFile, setActiveFile] = useState(null)
  const [isVisibleFileViewPopup, setIsVisibleFileViewPopup] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [commonFiles, setCommonFiles] = useState([])
  const { annex_attachments, code, status, action_history } =
    selectedElectronicDocument || {}

  // Định nghĩa electronicStatus (đồng bộ với quy trình tiêu chuẩn)
  const electronicStatus = [
    'PENDING',
    'CRE_APPROVAL',
    'VT_APPROVAL',
    'LEAD_APPROVAL',
    'REGIS_NUMBER',
    'READY',
    'RELEASE',
  ]

  // Sử dụng currentStep để xác định shouldShowFileAction (từ "Cấp số" trở đi)
  const shouldShowFileAction = currentStep >= 4 // Chỉ hiển thị từ bước "Cấp số"

  // Initialize fileList and fetch commonFiles
  useEffect(() => {
    let initialFiles = []
    if (commonFiles.length > 0) {
      initialFiles = commonFiles.map((cf, index) => ({
        uid: `common_${cf.fileId}_${index}`,
        id: cf.fileId,
        originalFileId: cf.fileId,
        name: cf.fileName,
        status: status,
        isExisting: true,
        isRegistered: cf.isRegisNumber || false,
        isSigned: cf.isSigned || false,
        registeredFileId: null,
        registeredFileName: null,
        signedFileId: null,
        signedFileName: null,
        type: cf.fileType || 'application/pdf',
      }))
    }
    if (
      annex_attachments &&
      Array.isArray(annex_attachments) &&
      annex_attachments.length > 0
    ) {
      annex_attachments.forEach(file => {
        const existingFile = initialFiles.find(f => f.id === file.file_id)
        if (existingFile) {
          existingFile.name = file.file_name || existingFile.name
          existingFile.type = file.type || existingFile.type
          existingFile.registeredFileId =
            file.registeredFileId || existingFile.registeredFileId
          existingFile.registeredFileName =
            file.registeredFileName || existingFile.registeredFileName
          existingFile.signedFileId =
            file.signedFileId || existingFile.signedFileId
          existingFile.signedFileName =
            file.signedFileName || existingFile.signedFileName
        } else {
          initialFiles.push({
            uid: `file_${file.file_id}`,
            id: file.file_id,
            originalFileId: file.file_id,
            name: file.file_name,
            status: status,
            isExisting: true,
            isRegistered: file.isRegistered || false,
            isSigned: file.isSigned || false,
            registeredFileId: file.registeredFileId || null,
            registeredFileName: file.registeredFileName || null,
            signedFileId: file.signedFileId || null,
            signedFileName: file.signedFileName || null,
            type: file.type || 'application/pdf',
          })
        }
      })
    }
    const uniqueFiles = Array.from(
      new Map(initialFiles.map(file => [file.id, file])).values()
    )
    setFileList(uniqueFiles)
  }, [annex_attachments, commonFiles, status])

  useEffect(() => {
    if (code) {
      commonFileStore
        .getCommonFileList({
          documentCode: code,
          documentType: 'ELEC_DOC_ANNEX_ATTACHED',
        })
        .then(response => {
          const updatedCommonFiles = response || []
          setCommonFiles(updatedCommonFiles)
        })
        .catch(error => {
          console.error('Error fetching common files:', error)
        })
    }
  }, [code, commonFileStore])

  const checkFileStatus = fileId => {
    const commonFile = commonFiles.find(cf => cf.fileId === fileId)
    return {
      exists: !!commonFile,
      isRegistered: commonFile ? commonFile.isRegisNumber : false,
      isSigned: commonFile ? commonFile.isSigned : false,
    }
  }

  const handleChangeAnnexFile = useCallback(
    info => {
      let newFileList = [...info.fileList]
      newFileList = newFileList.filter(file => {
        if (file.type !== 'application/pdf' && file.originFileObj) {
          message.error(
            `Tệp "${file.name}" không phải PDF. Chỉ hỗ trợ tệp PDF.`
          )
          return false
        }
        return true
      })
      newFileList = newFileList
        .map(file => {
          if (file.response && file.response.data) {
            const existingFile = fileList.find(
              f => f.id === file.response.data.file_id
            )
            if (existingFile) {
              message.error(
                `Tệp với ID ${file.response.data.file_id} đã tồn tại.`
              )
              return null
            }
            return {
              ...file,
              id: file.response.data.file_id,
              originalFileId: null,
              url: file.response.data.file_url,
              isRegistered: false,
              isSigned: false,
              registeredFileId: null,
              registeredFileName: null,
              signedFileId: null,
              signedFileName: null,
            }
          }
          return {
            ...file,
            isRegistered: file.isRegistered || false,
            isSigned: file.isSigned || false,
            registeredFileId: file.registeredFileId || null,
            registeredFileName: file.registeredFileName || null,
            signedFileId: file.signedFileId || null,
            signedFileName: file.signedFileName || null,
            originalFileId: file.originalFileId || file.id || null,
          }
        })
        .filter(file => file !== null)
      const filteredFileList = newFileList.filter(
        (elem, index, self) =>
          self.findIndex(
            file =>
              (file.name === elem.name || file.id === elem.id) &&
              file.uid === elem.uid
          ) === index
      )
      setFileList(filteredFileList)
    },
    [setFileList]
  )

  const handleRemoveFile = useCallback(
    fileToRemove => {
      Modal.confirm({
        title: 'Xác nhận',
        content: 'Bạn có chắc muốn xóa tệp này không?',
        okText: 'Đồng ý',
        cancelText: 'Hủy',
        onOk: () => {
          const updatedFileList = fileList.filter(
            file => file.uid !== fileToRemove.uid
          )
          setFileList(updatedFileList)
        },
      })
    },
    [fileList, setFileList]
  )

  const handleOpenRegisNumberModal = useCallback(
    async file => {
      const fileIdToCheck = file.isRegistered
        ? file.registeredFileId
        : file.id || file.originalFileId
      const status = checkFileStatus(fileIdToCheck)
      if (status.isRegistered) {
        message.info('Tệp đã được cấp số trước đó.')
        return
      }
      setActiveFile({
        ...file,
        fileId: file.isRegistered ? file.registeredFileId : file.id,
        originFileObj: file.originFileObj || null,
      })
      setIsRegisNumberModalVisible(true)
    },
    [setActiveFile, setIsRegisNumberModalVisible]
  )

  const handleCloseRegisNumberModal = useCallback(() => {
    setIsRegisNumberModalVisible(false)
    setActiveFile(null)
    fileStore.clearDocumentAttachment()
  }, [setActiveFile, setIsRegisNumberModalVisible, fileStore])

  const handleOpenSignModal = useCallback(
    async file => {
      const fileIdToCheck = file.isRegistered
        ? file.registeredFileId
        : file.id || file.originalFileId
      const status = checkFileStatus(fileIdToCheck)
      signedDocumentStore.clearDataPopupSign()
      fileStore.clearDocumentAttachment()
      setActiveFile({
        ...file,
        fileId: file.isRegistered ? file.registeredFileId : file.id,
        originFileObj: file.originFileObj || null,
      })
      setIsSignModalVisible(true)
    },
    [setActiveFile, setIsSignModalVisible, signedDocumentStore, fileStore]
  )

  const handleCloseSignModal = useCallback(() => {
    setIsSignModalVisible(false)
    setActiveFile(null)
    signedDocumentStore.clearDataPopupSign()
    fileStore.clearDocumentAttachment()
  }, [setActiveFile, setIsSignModalVisible, signedDocumentStore, fileStore])

  const handleAfterRegisNumber = useCallback(
    async (fileId, fileName) => {
      try {
        setFileList(prevFileList => {
          const fileExists = prevFileList.some(
            file => file.id === fileId || file.registeredFileId === fileId
          )
          if (fileExists) {
            message.warning(`Tệp với ID ${fileId} đã tồn tại trong danh sách.`)
            return prevFileList
          }
          return prevFileList.map(file =>
            file.uid === activeFile.uid
              ? {
                  ...file,
                  id: fileId,
                  name: fileName,
                  isRegistered: true,
                  registeredFileId: fileId,
                  registeredFileName: fileName,
                  originalFileId: file.originalFileId || file.id || null,
                }
              : file
          )
        })
      } catch (error) {
        console.error('Error updating after registration:', error)
      }
    },
    [activeFile, setFileList]
  )

  const handleAfterSignAnnex = useCallback(
    async ({ fileSignId, fileSignName }) => {
      try {
        setFileList(prevFileList => {
          const fileExists = prevFileList.some(
            file => file.signedFileId === fileSignId
          )
          if (fileExists) {
            message.warning(
              `Tệp với ID ${fileSignId} đã tồn tại trong danh sách.`
            )
            return prevFileList
          }
          return prevFileList.map(file =>
            file.uid === activeFile.uid
              ? {
                  ...file,
                  id: fileSignId,
                  name: fileSignName,
                  isSigned: true,
                  signedFileId: fileSignId,
                  signedFileName: fileSignName,
                  originalFileId:
                    file.originalFileId ||
                    file.id ||
                    file.registeredFileId ||
                    null,
                  registeredFileId: null,
                  registeredFileName: null,
                }
              : file
          )
        })
        handleCloseSignModal()
      } catch (error) {
        console.error('Error updating after signing:', error)
      }
    },
    [activeFile, setFileList, handleCloseSignModal]
  )

  const handleSubmit = useCallback(async () => {
    if (!code) {
      message.error('Mã tài liệu không hợp lệ.')
      return
    }
    try {
      loadingAnimationStore.showSpinner(true)

      // Handle new file uploads
      const newFiles = fileList.filter(
        file => !file.id && !file.isExisting && file.originFileObj
      )
      const uploadPromises = newFiles.map(file => {
        const formData = new FormData()
        formData.append('file', file.originFileObj)
        return fileStore.uploadFile(formData)
      })
      const uploadResponses = await Promise.all(uploadPromises)
      const newFileIds = uploadResponses.map(res => res.data.file_id)

      // Update fileList with new file IDs
      let updatedFileList = [...fileList]
      if (newFileIds.length > 0) {
        let fileIndex = 0
        updatedFileList = updatedFileList.map(file => {
          if (!file.id && !file.isExisting && file.originFileObj) {
            const newId = newFileIds[fileIndex]
            fileIndex += 1
            return {
              ...file,
              id: newId,
              originalFileId: null,
            }
          }
          return file
        })
        setFileList(updatedFileList)
      }

      // Initialize new_items and remove_items
      const newItems = []
      const removeItems = []
      const currentProcessedFileIds = new Set()

      // Get current annex_attachments file IDs from backend
      const originalFileIds = annex_attachments?.map(file => file.file_id) || []

      // Determine new_items and remove_items
      updatedFileList.forEach(file => {
        const fileIdToAdd = file.isSigned
          ? file.signedFileId
          : file.isRegistered
          ? file.registeredFileId
          : file.id

        // Only add to new_items if the file is new (not in originalFileIds) or has been modified (registered/signed)
        if (
          fileIdToAdd &&
          !originalFileIds.includes(fileIdToAdd) && // Ensure file is not already in annex_attachments
          !currentProcessedFileIds.has(fileIdToAdd) // Prevent duplicates in new_items
        ) {
          newItems.push(fileIdToAdd)
          currentProcessedFileIds.add(fileIdToAdd)
        }

        // Handle removal of replaced files (e.g., original or registered files replaced by signed files)
        if (file.isSigned && file.signedFileId) {
          if (
            file.registeredFileId &&
            file.registeredFileId !== file.signedFileId
          ) {
            removeItems.push(file.registeredFileId)
          }
          if (
            file.originalFileId &&
            file.originalFileId !== file.signedFileId
          ) {
            removeItems.push(file.originalFileId)
          }
          if (
            file.id &&
            file.id !== file.signedFileId &&
            file.id !== file.originalFileId
          ) {
            removeItems.push(file.id)
          }
        } else if (file.isRegistered && file.registeredFileId) {
          if (
            file.originalFileId &&
            file.originalFileId !== file.registeredFileId
          ) {
            removeItems.push(file.originalFileId)
          }
          if (
            file.id &&
            file.id !== file.registeredFileId &&
            file.id !== file.originalFileId
          ) {
            removeItems.push(file.id)
          }
        }

        // Track processed file IDs
        if (fileIdToAdd) {
          currentProcessedFileIds.add(fileIdToAdd)
        }
        if (file.originalFileId) {
          currentProcessedFileIds.add(file.originalFileId)
        }
      })

      // Identify files that were in original annex_attachments but are no longer in fileList
      const deletedOriginalFiles = originalFileIds.filter(
        fileId => !currentProcessedFileIds.has(fileId)
      )
      removeItems.push(...deletedOriginalFiles)

      // Deduplicate new_items and remove_items
      const uniqueNewItems = [...new Set(newItems)].filter(id => id != null)
      const uniqueRemovedFileIds = [...new Set(removeItems)]

      // Check if there are any changes to submit
      if (uniqueNewItems.length === 0 && uniqueRemovedFileIds.length === 0) {
        message.info('Không có thay đổi nào để cập nhật.')
        return
      }

      // Prepare payload
      const submitData = {
        annex_attachments: {
          new_items: uniqueNewItems,
          remove_items: uniqueRemovedFileIds,
        },
        action: 'ANNEX_DOC',
      }

      // Submit update to backend
      await electronicDocumentStore.updateElectronicAnnexDocument(
        code,
        submitData
      )

      // Update common files' metadata (isRegistered, isSigned)
      const filesToUpdate = updatedFileList.filter(
        file => file.isRegistered || file.isSigned
      )
      const updatePromises = filesToUpdate.map(file => {
        const fileIdToUpdate = file.isSigned
          ? file.signedFileId
          : file.isRegistered
          ? file.registeredFileId
          : file.id
        if (fileIdToUpdate) {
          const payload = {
            fileIds: [fileIdToUpdate],
            documentType: 'ELEC_DOC_ANNEX_ATTACHED',
            isRegisNumber: file.isRegistered || false,
            isSigned: file.isSigned || false,
          }
          return commonFileStore.updateFilesByUser(payload)
        }
        return Promise.resolve()
      })
      await Promise.all(updatePromises)

      // Refresh common files
      const updatedCommonFiles = await commonFileStore.getCommonFileList({
        documentCode: code,
        documentType: 'ELEC_DOC_ANNEX_ATTACHED',
      })
      setCommonFiles(updatedCommonFiles?.fileLists || [])

      message.success('Cập nhật phụ lục thành công!')
      await getElectronicDocumentById()
      await listHistoryLogStore.getLists(code)
      onClose()
    } catch (err) {
      console.error('Error updating annex document:', err)
    } finally {
      loadingAnimationStore.showSpinner(false)
    }
  }, [
    code,
    fileList,
    fileStore,
    loadingAnimationStore,
    annex_attachments,
    electronicDocumentStore,
    getElectronicDocumentById,
    onClose,
    commonFileStore,
    listHistoryLogStore,
  ])

  const uploadProps = {
    fileList,
    multiple: true,
    accept: '.pdf',
    beforeUpload: file => {
      if (file.type !== 'application/pdf') {
        message.error('Chỉ hỗ trợ tệp PDF!')
        return Upload.LIST_IGNORE
      }
      return false
    },
    onChange: handleChangeAnnexFile,
    onRemove: handleRemoveFile,
    showUploadList: false,
  }

  const renderedFileItems = useMemo(() => {
    console.log(
      'Rendering file items - shouldShowFileAction:',
      shouldShowFileAction
    )
    if (!fileList) return null
    return fileList.map(file => {
      const fileIdToCheck = file.isSigned
        ? file.signedFileId
        : file.isRegistered
        ? file.registeredFileId
        : file.id || file.originalFileId
      const commonFile = commonFiles.find(cf => cf.fileId === fileIdToCheck)
      const isRegistered =
        file.isRegistered || (commonFile ? commonFile.isRegisNumber : false)
      const isSigned =
        file.isSigned || (commonFile ? commonFile.isSigned : false)

      return (
        <FileItem key={file.uid}>
          <span>
            <FileTextOutlined style={{ marginRight: 5 }} />
            {trimOverLengthString(
              file.signedFileName || file.registeredFileName || file.name,
              70
            )}
          </span>
          {shouldShowFileAction ? (
            <FileAction>
              {isRegistered ? (
                <Tooltip title="Đã cấp số">
                  <Button
                    type="text"
                    disabled
                    style={{ marginRight: 10, color: '#52c41a' }}>
                    Đã cấp số
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title="Cấp số">
                  <Button
                    type="text"
                    onClick={() => handleOpenRegisNumberModal(file)}
                    style={{ marginRight: 10 }}>
                    Cấp số
                  </Button>
                </Tooltip>
              )}
              {isSigned ? (
                <Tooltip title="Đã ký số">
                  <Button
                    type="text"
                    disabled
                    style={{ marginRight: 10, color: '#52c41a' }}>
                    Đã ký số
                  </Button>
                </Tooltip>
              ) : (
                <Tooltip title="Ký số">
                  <Button
                    type="text"
                    onClick={() => handleOpenSignModal(file)}
                    style={{ marginRight: 10 }}>
                    Ký số
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Xóa">
                <Button
                  type="text"
                  onClick={() => handleRemoveFile(file)}
                  icon={<DeleteOutlined style={{ color: redPrimary }} />}
                />
              </Tooltip>
            </FileAction>
          ) : (
            <FileAction>
              <Tooltip title="Xóa">
                <Button
                  type="text"
                  onClick={() => handleRemoveFile(file)}
                  icon={<DeleteOutlined style={{ color: redPrimary }} />}
                />
              </Tooltip>
            </FileAction>
          )}
        </FileItem>
      )
    })
  }, [
    fileList,
    shouldShowFileAction,
    handleOpenRegisNumberModal,
    handleOpenSignModal,
    handleRemoveFile,
    redPrimary,
    commonFiles,
  ])

  const selectedViewFile = useMemo(() => {
    if (!selectedFile) return null
    return {
      file_name:
        selectedFile.signedFileName ||
        selectedFile.registeredFileName ||
        selectedFile.name,
      file_id: selectedFile.isSigned
        ? selectedFile.signedFileId
        : selectedFile.isRegistered
        ? selectedFile.registeredFileId
        : selectedFile.id,
    }
  }, [selectedFile])

  return (
    <>
      <Modal
        title="Cập nhật phụ lục đi kèm"
        visible={visible}
        onCancel={onClose}
        closable={true}
        footer={[
          <Button key="cancel" danger onClick={onClose}>
            Hủy bỏ
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit}>
            Cập nhật
          </Button>,
        ]}
        width={800}>
        <Typography.Title level={4}>Phụ lục đi kèm</Typography.Title>
        <Upload {...uploadProps}>
          <Button icon={<UploadOutlined />}>Chọn phụ lục đi kèm</Button>
        </Upload>
        <SignDocumentList>{renderedFileItems}</SignDocumentList>
      </Modal>
      <PopupRegisNumberAnnexDoc
        isVisiblePopupRegisNumber={isRegisNumberModalVisible}
        handleClosePopupRegisNumber={handleCloseRegisNumberModal}
        fileActive={activeFile}
        handleAfterRegisNumber={handleAfterRegisNumber}
        isElecDocument={true}
      />
      {isSignModalVisible && (
        <ModalAnnexSign
          isVisiblePopupSign={isSignModalVisible}
          handleClosePopupSign={handleCloseSignModal}
          fileId={activeFile?.fileId}
          activeFile={activeFile}
          fileStore={fileStore}
          handleAfterSignAnnex={handleAfterSignAnnex}
          hiddenSelectDocument={true}
        />
      )}
    </>
  )
}

export default memo(
  inject(
    'fileStore',
    'loadingAnimationStore',
    'electronicDocumentStore',
    'signedDocumentStore',
    'listHistoryLogStore',
    'commonFileStore'
  )(observer(InternalDocumentSignDocUpdateAnnex))
)
